import { Link } from 'react-router-dom';
import { CITIES } from '@/config/platform';
import { ROUTES } from '@/config/navigation';
import { BRAND } from '@/config/brand';
import { DbLockup } from '@/components/ui/Logo';

/**
 * Les cinq choses qu'on peut faire avec une voiture sur la plateforme, et où
 * chacune mène réellement.
 *
 * Le paramètre de ville n'est pas le même partout : la location l'appelle
 * `lieu`, la vente et les prestataires l'appellent `ville`. On garde le nom que
 * chaque écran attend plutôt que d'en imposer un troisième — un lien qui arrive
 * avec la mauvaise clé affiche « toutes les villes » sans prévenir personne.
 */
const CITY_SERVICES: {
  title: string;
  label: (city: string) => string;
  to: (slug: string) => string;
  showSoon?: boolean;
}[] = [
  {
    title: 'Louer une voiture',
    label: (city) => `Location de voiture à ${city}`,
    to: (slug) => `${ROUTES.search}?lieu=${slug}`,
    showSoon: true,
  },
  {
    title: 'Acheter une voiture',
    label: (city) => `Acheter une voiture à ${city}`,
    to: (slug) => `${ROUTES.listings}?ville=${slug}`,
  },
  {
    title: 'Vendre une voiture',
    label: (city) => `Vendre une voiture à ${city}`,
    to: (slug) => `${ROUTES.becomeSeller}?ville=${slug}`,
  },
  {
    title: 'Réparer ma voiture',
    label: (city) => `Réparer ma voiture à ${city}`,
    to: (slug) => `${ROUTES.garages}?ville=${slug}`,
  },
  {
    title: 'Laver ma voiture',
    label: (city) => `Laver ma voiture à ${city}`,
    to: (slug) => `${ROUTES.carwashes}?ville=${slug}`,
  },
];

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-ink-200 bg-ink-50">
      <div className="cf-container grid gap-8 py-12 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        <div>
          <span className="inline-flex">
            <DbLockup className="h-[72px] w-auto" />
          </span>
          <p className="mt-3 max-w-[32ch] text-[13px] leading-relaxed text-ink-500">
            Louer, acheter et vendre une voiture en Côte d’Ivoire. Agences vérifiées, annonces
            contrôlées, paiement en espèces. Gratuit, sans commission.
          </p>
        </div>

        <nav aria-label="Plateforme">
          <p className="text-[13px] font-bold text-ink-950">Plateforme</p>
          <ul className="mt-3.5 space-y-2.5 text-[13px] text-ink-700">
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.howItWorks}>
                Comment ça marche
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.becomePartner}>
                Devenir partenaire
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.search}>
                Rechercher un véhicule
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.listings}>
                Voitures à vendre
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.becomeSeller}>
                Vendre ma voiture
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.garages}>
                Garages mécaniques
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.carwashes}>
                Lavage auto
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.becomeProvider}>
                Inscrire mon garage
              </Link>
            </li>
          </ul>
        </nav>

        <div>
          <p className="text-[13px] font-bold text-ink-950">Légal</p>
          <ul className="mt-3.5 space-y-2.5 text-[13px] text-ink-700">
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.legal.terms}>
                Conditions générales
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.legal.privacy}>
                Politique de confidentialité
              </Link>
            </li>
            <li>
              <Link className="hover:text-ink-900" to={ROUTES.legal.notice}>
                Mentions légales
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Les cinq services, ville par ville                                  */}
      {/* ------------------------------------------------------------------ */}
      {/*
        Vingt-cinq liens : c'est beaucoup pour un pied de page, et c'est
        exactement ce qu'on attend de lui. « garage Abidjan », « lavage auto
        Cocody », « voiture à vendre Bouaké » sont des recherches distinctes sur
        Google, et chacune veut sa propre porte d'entrée.

        Chaque lien mène à l'écran QUI FAIT la chose, avec la ville déjà
        sélectionnée — pas à une page de présentation. Le visiteur qui clique
        « Réparer ma voiture à Abidjan » veut voir des garages, pas lire ce
        qu'est un garage.

        Masqué sous 640 px, comme la colonne « Villes » qu'il remplace : sur un
        téléphone, cette liste ferait défiler dans le vide. Masqué en CSS et non
        retiré du rendu — les liens restent dans le HTML, donc suivis par les
        moteurs de recherche.
      */}
      <div className="hidden border-t border-ink-200 sm:block">
        <div className="cf-container py-10">
          <p className="text-[13px] font-bold text-ink-950">Nos services, ville par ville</p>
          <div className="mt-5 grid gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {CITY_SERVICES.map((service) => (
              <nav key={service.title} aria-label={service.title}>
                <p className="text-[13px] font-semibold text-ink-900">{service.title}</p>
                <ul className="mt-2.5 space-y-2 text-[13px] text-ink-600">
                  {CITIES.map((city) => (
                    <li key={city.slug}>
                      <Link className="hover:text-ink-900" to={service.to(city.slug)}>
                        {service.label(city.label)}
                        {/* « Bientôt » ne concerne que la location : l'annuaire
                            des garages et les annonces fonctionnent partout,
                            même là où aucune agence n'est encore installée. */}
                        {service.showSoon && !city.active && (
                          <span className="ml-1 text-xs text-ink-400">(bientôt)</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          {/*
            Les pages villes — /location-voiture-abidjan et les autres — sont
            nos pages de référencement : titre, description et données
            structurées propres à chaque ville. Les liens ci-dessus mènent
            volontairement aux écrans qui FONT la chose, pas à ces pages-là.

            Sans cette ligne, elles n'auraient plus qu'un seul lien entrant,
            depuis la section « Villes desservies » de l'accueil. Ici, elles en
            reçoivent un depuis CHAQUE page du site : c'est ce qui dit à Google
            qu'elles comptent.
          */}
          <nav aria-label="Pages villes" className="mt-8 border-t border-ink-200 pt-5">
            <p className="text-[13px] font-semibold text-ink-900">Nos villes</p>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-ink-600">
              {CITIES.map((city) => (
                <li key={city.slug}>
                  <Link className="hover:text-ink-900" to={ROUTES.cityLanding(city.slug)}>
                    {city.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      <div className="border-t border-ink-200">
        <div className="cf-container py-4 text-xs text-ink-500">
          © {year} {BRAND.name}. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
}
